import type { ImportDiscovery, ImportMapping } from '@servicetim/shared';
import {
  useImportMapping,
  useIssueTypes,
  usePriorities,
  useProjectFields,
  usePutImportMapping,
  useUsers,
  useWorkflow,
} from '@servicetim/ui/api';
import { Button, LoadingState, ProblemBanner, toast } from '@servicetim/ui/components';
import { useEffect, useMemo, useState } from 'react';
import { MappingTabs } from './mapping-tabs';
import { withSuggestedDefaults } from './suggest';

export interface MappingPanelProps {
  importId: string;
  projectId: string;
  discovery: ImportDiscovery;
}

/**
 * Wires `MappingTabs` (pure) to the target lists (project fields, the primary workflow's
 * statuses — same "first, lowest-position issue type's workflow" the run executor creates
 * drafts against, see `packages/jobs/src/imports/run.ts`), priorities, tenant users and issue
 * types, plus the saved mapping. Local `draft` state starts from the saved mapping with S-09-03
 * suggestions filled in for anything not already mapped, and only reaches the server on Save.
 */
export function MappingPanel({ importId, projectId, discovery }: MappingPanelProps) {
  const savedMapping = useImportMapping(importId);
  const fields = useProjectFields(projectId);
  const issueTypes = useIssueTypes(projectId);
  const priorities = usePriorities(projectId);
  const users = useUsers({ pageSize: 200 });
  const primaryWorkflowId = useMemo(
    () => [...(issueTypes.data ?? [])].sort((a, b) => a.position - b.position)[0]?.workflowId,
    [issueTypes.data],
  );
  const workflow = useWorkflow(projectId, primaryWorkflowId);
  const putMapping = usePutImportMapping(importId);

  const targetsReady =
    !fields.isPending && !issueTypes.isPending && !priorities.isPending && !users.isPending;
  const statusesReady = primaryWorkflowId === undefined || !workflow.isPending;

  const [draft, setDraft] = useState<ImportMapping | null>(null);
  useEffect(() => {
    if (draft !== null || savedMapping.data === undefined || !targetsReady || !statusesReady) {
      return;
    }
    setDraft(
      withSuggestedDefaults(
        discovery,
        {
          fields: fields.items,
          statuses: workflow.data?.draft.statuses ?? [],
          priorities: priorities.data ?? [],
          users: users.items,
          issueTypes: issueTypes.data ?? [],
        },
        savedMapping.data,
      ),
    );
  }, [
    draft,
    savedMapping.data,
    targetsReady,
    statusesReady,
    discovery,
    fields.items,
    workflow.data,
    priorities.data,
    users.items,
    issueTypes.data,
  ]);

  if (savedMapping.error) {
    return <ProblemBanner error={savedMapping.error} onRetry={() => void savedMapping.refetch()} />;
  }
  if (draft === null) {
    return <LoadingState rows={6} label="Loading the mapping…" />;
  }

  const save = async () => {
    try {
      const saved = await putMapping.mutateAsync(draft);
      setDraft(saved);
      toast.success('Mapping saved');
    } catch {
      // Shown by the banner below (field errors by path, e.g. statuses.src-open.statusId).
    }
  };

  return (
    <div className="space-y-4">
      {putMapping.error ? <ProblemBanner error={putMapping.error} size="sm" /> : null}
      <MappingTabs
        discovery={discovery}
        mapping={draft}
        onChange={setDraft}
        targets={{
          fields: fields.items,
          statuses: workflow.data?.draft.statuses ?? [],
          priorities: priorities.data ?? [],
          users: users.items,
          issueTypes: issueTypes.data ?? [],
        }}
      />
      <div className="flex justify-end">
        <Button onClick={() => void save()} disabled={putMapping.isPending}>
          Save mapping
        </Button>
      </div>
    </div>
  );
}
